import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Clock, Users, Star, CheckCircle, Play, Download, ArrowRight } from 'lucide-react';
import { api } from '../../api';

interface Formation {
  id: string;
  title: string;
  description: string;
  longDescription: string;
  duration: string;
  level: string;
  price: string;
  originalPrice: string;
  category: string;
  rating: number;
  students: number;
  image: string;
  modules: number;
  quizzes: number;
  certificate: boolean;
  highlights: string[];
  instructor: string;
  instructorBio: string;
}

const ShowcaseFormations: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [formations, setFormations] = useState<Formation[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchFormations = async () => {
      try {
        const response = await api.get('/siteContent/formations');
        setFormations(response.data || []);
      } catch (err) {
        setError('Failed to fetch formations.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchFormations();
  }, []);

  const categories = [
    { id: 'all', name: 'Toutes les formations', count: (formations || []).length },
    { id: 'gps-analysis', name: 'Analyse GPS', count: (formations || []).filter(f => f.category === 'gps-analysis').length },
    { id: 'training-load', name: 'Charge d\'entraînement', count: (formations || []).filter(f => f.category === 'training-load').length },
    { id: 'rehabilitation', name: 'Expert réathlétisation', count: (formations || []).filter(f => f.category === 'rehabilitation').length },
    { id: 'management', name: 'Management', count: (formations || []).filter(f => f.category === 'management').length },
    { id: 'data', name: 'Data & Bureautique', count: (formations || []).filter(f => f.category === 'data').length },
    { id: 'digital', name: 'Digital', count: (formations || []).filter(f => f.category === 'digital').length },
    { id: 'soft-skills', name: 'Soft skills', count: (formations || []).filter(f => f.category === 'soft-skills').length },
    { id: 'finance', name: 'Finance', count: (formations || []).filter(f => f.category === 'finance').length },
    { id: 'inclusion-numerique', name: 'Inclusion numérique', count: (formations || []).filter(f => f.category === 'inclusion-numerique').length }
  ];

  const filteredFormations = selectedCategory === 'all' 
    ? formations 
    : formations.filter(f => f.category === selectedCategory);

  const [selectedFormation, setSelectedFormation] = useState<string | null>(null);

  // Regroupement par catégorie (vue "Toutes") : une section par catégorie
  const CATEGORY_ORDER = ['gps-analysis', 'training-load', 'rehabilitation', 'management', 'data', 'digital', 'soft-skills', 'finance', 'inclusion-numerique'];
  const CATEGORY_NAME: Record<string, string> = {};
  categories.forEach(c => { if (c.id !== 'all') CATEGORY_NAME[c.id] = c.name; });
  const groupedFormations = CATEGORY_ORDER
    .map(catId => ({
      id: catId,
      name: CATEGORY_NAME[catId] || catId,
      items: (formations || []).filter(f => f.category === catId)
    }))
    .filter(g => g.items.length > 0);

  const renderCard = (formation: Formation) => (
        <div key={formation.id} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
                <div className="relative">
                  <img
                    src={formation.image}
                    alt={formation.title}
                    className="w-full h-48 object-cover"
                  />
                  {formation.originalPrice && formation.price && (
                    <div className="absolute top-4 left-4 bg-[#FFB639] text-white px-3 py-1 rounded-full text-sm font-semibold">
                      -{Math.round(((parseFloat(String(formation.originalPrice).replace('€', '')) - parseFloat(String(formation.price).replace('€', ''))) / parseFloat(String(formation.originalPrice).replace('€', ''))) * 100)}%
                    </div>
                  )}
                  <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-semibold text-gray-900">
                    {formation.level}
                  </div>
                  <div className="absolute bottom-4 left-4 bg-black/70 text-white px-3 py-1 rounded-full text-sm flex items-center">
                    <Play className="h-4 w-4 mr-1" />
                    {formation.duration}
                  </div>
                </div>
                
                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center">
                      <Star className="h-4 w-4 text-[#FFB639] " />
                      <span className="text-sm font-medium text-gray-700 ml-1">{formation.rating}</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-500">
                      <Users className="h-4 w-4 mr-1" />
                      {formation.students} étudiants
                    </div>
                  </div>

                  <h3 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2">
                    {formation.title}
                  </h3>
                  <p className="text-gray-600 mb-4 line-clamp-3">
                    {formation.description}
                  </p>

                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <span>{formation.modules} modules</span>
                      <span>{formation.quizzes} quiz</span>
                      {formation.certificate && (
                        <div className="flex items-center">
                          <CheckCircle className="h-4 w-4 text-[#FFB639] mr-1" />
                          <span>Certificat</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <span className="text-2xl font-bold text-[#FFB639]">{formation.price}</span>
                      <span className="text-sm text-gray-500 line-through ml-2">{formation.originalPrice}</span>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <button
                      onClick={() => setSelectedFormation(formation.id)}
                      className="w-full bg-gray-100 text-gray-700 py-3 px-4 rounded-lg font-semibold hover:bg-gray-200 transition-colors duration-300"
                    >
                      Voir les détails
                    </button>
                    <Link
                      to="/login"
                      className="w-full bg-[#FFB639] text-white py-3 px-4 rounded-lg font-semibold hover:bg-[#e5a333] transition-colors duration-300 text-center block"
                    >
                      S'inscrire maintenant
                    </Link>
                  </div>
                </div>
              </div>
  );

  if (loading) {
    return <div>Loading formations...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}

     {/* <section className="bg-gradient-to-br from-gray-900 to-black text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl lg:text-6xl font-bold mb-6">
              Nos <span className="text-[#FFB639]">Formations</span> d'Expert
            </h1>
            <p className="text-xl lg:text-2xl text-gray-300 max-w-4xl mx-auto mb-8">
              Des parcours conçus pour monter en compétences concrètement : expertise métier, méthodes éprouvées et accompagnement tout au long de votre montée en puissance
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <div className="flex items-center text-[#FFB639]">
                <CheckCircle className="h-5 w-5 mr-2" />
                <span>Formation</span>
              </div>
              <div className="flex items-center text-[#FFB639]">
                <CheckCircle className="h-5 w-5 mr-2" />
                <span>Formation en ligne</span>
              </div>
              <div className="flex items-center text-[#FFB639]">
                <CheckCircle className="h-5 w-5 mr-2" />
                <span>Formation pratique</span>
              </div>
            </div>
          </div>
        </div>
      </section>*/}
       <section className="relative bg-gradient-to-br from-gray-900 via-gray-400 to-black text-white overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-50"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#FFB639]/20 to-transparent"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="space-y-4">
                <h1 className="text-4xl lg:text-6xl font-bold leading-tight">
                   Nos <span className="text-[#FFB639]">Formations</span>
                  <p className="text-xl lg:text-2xl text-gray-300 max-w-4xl mx-auto mb-8">
                    Des parcours conçus pour monter en compétences concrètement : expertise métier, méthodes éprouvées et accompagnement tout au long de votre montée en puissance
                  </p>
                </h1>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/login"
                  className="inline-flex items-center px-8 py-4 bg-[#FFB639] text-white font-semibold rounded-lg hover:bg-[#e5a333] transition-all duration-300 transform hover:scale-105 shadow-lg"
                >
                  Accéder à la plateforme
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
                <button className="inline-flex items-center px-8 py-4 border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-gray-900 transition-all duration-300">
                  <Play className="mr-2 h-5 w-5" />
                  Voir la démo
                </button>
              </div>

              <div className="flex items-center space-x-6 text-sm">
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-[#FFB639] mr-2" />
                  <span></span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-[#FFB639] mr-2" />
                  <span>Accès à vie</span>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="relative z-10">
                <img
                  src="logo.png"
                  alt="Formation football"
                  className="rounded-2xl shadow-2xl"
                />
                <div className="absolute -bottom-6 -right-6 bg-[#FFB639] text-white p-6 rounded-xl shadow-lg">
                  <div className="text-2xl font-bold">400+</div>
                  <div className="text-sm">Clubs partenaires</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories Selector */}
      <section className="py-10 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center gap-3">
            <label htmlFor="category-select" className="text-sm font-semibold text-gray-700">
              Filtrer par catégorie :
            </label>
            <select
              id="category-select"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-5 py-3 rounded-lg border-2 border-gray-200 bg-white text-gray-800 font-medium shadow-sm focus:outline-none focus:border-[#FFB639] focus:ring-2 focus:ring-[#FFB639]/20 transition-all duration-300 min-w-[260px]"
            >
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name} ({category.count})
                </option>
              ))}
            </select>
          </div>
        </div>
      </section>

      {/* Formations Grid - regroupee par categorie (vue Toutes) */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {selectedCategory === 'all' ? (
            <div className="space-y-16">
              {groupedFormations.map((group) => (
                <div key={group.id}>
                  <div className="flex items-center justify-between mb-8">
                    <h2 className="text-2xl lg:text-3xl font-bold text-gray-900">{group.name}</h2>
                    <span className="text-sm font-semibold text-gray-500">{group.items.length} formation{group.items.length > 1 ? 's' : ''}</span>
                  </div>
                  <div className="w-20 h-1 bg-[#FFB639] rounded-full mb-8"></div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8">
                    {group.items.map((formation) => renderCard(formation))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-2xl lg:text-3xl font-bold text-gray-900">{categories.find(c => c.id === selectedCategory)?.name}</h2>
                <span className="text-sm font-semibold text-gray-500">{filteredFormations.length} formation{filteredFormations.length > 1 ? 's' : ''}</span>
              </div>
              <div className="w-20 h-1 bg-[#FFB639] rounded-full mb-8"></div>
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8">
                {filteredFormations.map((formation) => renderCard(formation))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Formation Detail Modal */}
      {selectedFormation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {(() => {
              const formation = formations.find(f => f.id === selectedFormation);
              if (!formation) return null;

              return (
                <>
                  <div className="relative">
                    <img
                      src={formation.image}
                      alt={formation.title}
                      className="w-full h-64 object-cover"
                    />
                    <button
                      onClick={() => setSelectedFormation(null)}
                      className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm p-2 rounded-full hover:bg-white transition-colors"
                    >
                      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  <div className="p-8">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                      <div className="lg:col-span-2">
                        <h2 className="text-3xl font-bold text-gray-900 mb-4">
                          {formation.title}
                        </h2>
                        <p className="text-lg text-gray-600 mb-6">
                          {formation.longDescription}
                        </p>

                        <h3 className="text-xl font-bold text-gray-900 mb-4">
                          Ce que vous allez apprendre :
                        </h3>
                        <ul className="space-y-3 mb-8">
                          {formation.highlights.map((highlight, index) => (
                            <li key={index} className="flex items-start">
                              <CheckCircle className="h-5 w-5 text-[#FFB639] mr-3 mt-0.5 flex-shrink-0" />
                              <span className="text-gray-700">{highlight}</span>
                            </li>
                          ))}
                        </ul>

                        <div className="bg-gray-50 p-6 rounded-xl">
                          <h4 className="font-semibold text-gray-900 mb-2">Votre instructeur</h4>
                          <div className="flex items-center space-x-3">
                            <div className="w-12 h-12 bg-[#FFB639]/15 rounded-full flex items-center justify-center">
                              <span className="text-[#FFB639] font-bold">
                                {formation.instructor.split(' ').map(n => n[0]).join('')}
                              </span>
                            </div>
                            <div>
                              <div className="font-medium text-gray-900">{formation.instructor}</div>
                              <div className="text-sm text-gray-600">{formation.instructorBio}</div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="lg:col-span-1">
                        <div className="bg-gray-50 p-6 rounded-xl sticky top-4">
                          <div className="text-center mb-6">
                            <div className="text-3xl font-bold text-[#FFB639] mb-2">
                              {formation.price}
                            </div>
                            <div className="text-lg text-gray-500 line-through">
                              {formation.originalPrice}
                            </div>
                          </div>

                          <div className="space-y-4 mb-6">
                            <div className="flex items-center justify-between">
                              <span className="text-gray-600">Durée :</span>
                              <span className="font-medium">{formation.duration}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-gray-600">Niveau :</span>
                              <span className="font-medium">{formation.level}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-gray-600">Modules :</span>
                              <span className="font-medium">{formation.modules}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-gray-600">Quiz :</span>
                              <span className="font-medium">{formation.quizzes}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-gray-600">Étudiants :</span>
                              <span className="font-medium">{formation.students}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-gray-600">Note :</span>
                              <div className="flex items-center">
                                <Star className="h-4 w-4 text-[#FFB639]  mr-1" />
                                <span className="font-medium">{formation.rating}</span>
                              </div>
                            </div>
                          </div>

                          <div className="space-y-3">
                            <Link
                              to="/login"
                              className="w-full bg-[#FFB639] text-white py-3 px-4 rounded-lg font-semibold hover:bg-[#e5a333] transition-colors duration-300 text-center block"
                            >
                              S'inscrire maintenant
                            </Link>
                            <button className="w-full bg-gray-200 text-gray-700 py-3 px-4 rounded-lg font-semibold hover:bg-gray-300 transition-colors duration-300 flex items-center justify-center">
                              <Download className="h-4 w-4 mr-2" />
                              Télécharger le programme
                            </button>
                          </div>

                          <div className="mt-6 text-center">
                            <div className="flex items-center justify-center text-[#FFB639] mb-2">
                              <CheckCircle className="h-5 w-5 mr-2" />
                              <span className="text-sm font-medium">Accès à vie</span>
                            </div>
                            <div className="flex items-center justify-center text-[#FFB639] mb-2">
                              <CheckCircle className="h-5 w-5 mr-2" />
                              <span className="text-sm font-medium">Certificat inclus</span>
                            </div>
                            <div className="flex items-center justify-center text-[#FFB639]">
                              <CheckCircle className="h-5 w-5 mr-2" />
                              <span className="text-sm font-medium">Paiement en 12 fois</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* Financing Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Financement
            </h2>
            <p className="text-xl text-gray-600">
              Formations éligibles à la prise en charge professionnelle
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-2xl shadow-lg text-center">
              <div className="w-16 h-16 bg-[#FFB639]/15 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl">💼</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">OPCO</h3>
              <p className="text-gray-600">
                Prise en charge par votre OPCO pour les salariés et dirigeants
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg text-center">
              <div className="w-16 h-16 bg-[#FFB639]/15 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl">🎓</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">CPF</h3>
              <p className="text-gray-600">
                Utilisez votre Compte Personnel de Formation pour financer votre parcours
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg text-center">
              <div className="w-16 h-16 bg-[#FFB639]/15 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl">💳</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Paiement échelonné</h3>
              <p className="text-gray-600">
                Réglez en 3, 6 ou 12 fois sans frais pour faciliter votre investissement
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-gray-900 to-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-4">
            Prêt à devenir un expert ?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
            Rejoignez les centaines de professionnels qui ont déjà transformé leur parcours avec nos formations
          </p>
          <Link
            to="/login"
            className="inline-flex items-center px-8 py-4 bg-[#FFB639] text-white font-semibold rounded-lg hover:bg-[#e5a333] transition-all duration-300 transform hover:scale-105"
          >
            Accéder à la plateforme
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ShowcaseFormations;
